'use client';

import { SearchIcon } from 'assets/icons';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { searchFood } from 'utils/foodGlucoseDbHelper';
import { Container } from '..';
import { FoodListElement } from './FoodListElement';
import { SuggestiveChips } from './SuggestiveChips';
import styles from './SearchComponent.module.css';

const cx = (...args: Array<string | false | undefined | null>) =>
  args.filter(Boolean).join(' ');

export const SearchComponent = ({
  className,
  defaultSearchString,
}: {
  className?: string;
  defaultSearchString?: string;
}) => {
  const [search, setSearch] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>(
    defaultSearchString ?? '',
  );
  const [searchResponse, setSearchResponse] = useState<any>(null);

  const autocompleteTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!search) setSearchResponse(null);
  }, [search]);

  const onSearchClick = () => {
    setSearch(true);
  };

  const handleAutoSuggestion = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    autocompleteTimer.current && clearTimeout(autocompleteTimer.current);

    autocompleteTimer.current = setTimeout(() => {
      if (e.target.value.length >= 3) {
        searchFood({ query: e.target.value, count: 4, page: 1 })
          .then((res) => {
            setSearchResponse(res);
          })
          .catch((e) => {
            console.log(e);
          });
      }
    }, 300);
  };

  const router = useRouter();

  const searchNavigate = (input?: string) => {
    if (input) setSearchString(input);
    router.push(`/ogdb/search?q=${encodeURIComponent(input ?? '')}`);
  };

  const handleOnKeyDown = (e: any) => {
    if (e.keyCode !== 13 || searchString.length < 3) return;
    setSearch(false);
    router.push(`/ogdb/search?q=${encodeURIComponent(searchString)}`);
  };

  return (
    <div className={cx(styles.root, className)}>
      {search ? (
        <div
          className={styles.mask}
          onClick={() => setSearch(false)}
          role="button"
          tabIndex={0}
        />
      ) : null}
      {!search ? (
        <div className={styles.searchFood}>
          <input
            type="text"
            placeholder="Search for food"
            onClick={onSearchClick}
            value={searchString}
            // onKeyDown={handleOnKeyDown}
          />
          <SearchIcon className={styles.search} />
          <div className={styles.chipsContainer}>
            <SuggestiveChips
              onClick={(data) => {
                searchNavigate(data);
              }}
              data={[
                { emoji: '🍕', title: 'Pizza' },
                { emoji: '🍔', title: 'Burger' },
                { emoji: '🍲', title: 'Poha' },
                { emoji: '🍦', title: 'Ice Cream' },
                { emoji: '💪🏻', title: 'Protein' },
                { emoji: '🫓', title: 'Dosa' },
                { emoji: '🌯', title: 'Roll' },
                { emoji: '🥘', title: 'Biryani' },
                { emoji: '🥗', title: 'Salad' },
              ]}
            />
          </div>
          <div className={styles.chipsContainer}>
            <SuggestiveChips
              onClick={(data) => {
                searchNavigate(data);
              }}
              data={[
                { emoji: '🫓', title: 'Paratha' },
                { emoji: '🌯', title: 'Burrito' },
                { emoji: '🥖', title: 'Sub' },
                { emoji: '🥪', title: 'Sandwich' },
                { emoji: '🍳', title: 'Omlette' },
                { emoji: '🥛', title: 'Whey' },
                { emoji: '🍜', title: 'Noodle' },
                { emoji: '🫓', title: 'Idli' },
                { emoji: '🥗', title: 'Chicken Salad' },
              ]}
            />
          </div>
        </div>
      ) : (
        <div className={styles.searchFoodOverlay}>
          <input
            type="text"
            placeholder="Search for food"
            onChange={handleAutoSuggestion}
            onKeyDown={handleOnKeyDown}
          />
          <button
            className={styles.searchBtn}
            onClick={() => {
              searchNavigate(searchString);
              setSearch(false);
            }}
          >
            <SearchIcon fill="#ffffff" />
          </button>
          {searchString.length > 0 ? (
            <Container style={{ padding: '0' }}>
              <div className={styles.suggestions}>
                {searchResponse?.length > 0 ? (
                  <>
                    {searchResponse?.map((e: any, i: number) => {
                      return (
                        <>
                          <FoodListElement
                            key={i}
                            foodTitle={e.name}
                            foodPeakValue={e.average_peak_glucose_levels}
                            foodScore={Math.round(e.average_score)}
                            foodSpikeTrend={e.spike_indicator}
                            mealTime={e.meal_timing_to_show_at}
                            href={{
                              pathname: `/ogdb/${
                                e.url_friendly_name ?? e.food_id
                              }`,
                            }}
                          />
                          <hr className={styles.elementDivider} />
                        </>
                      );
                    })}
                  </>
                ) : null}
              </div>
            </Container>
          ) : null}
        </div>
      )}
    </div>
  );
};
